import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState, IHubProtocol, IRetryPolicy, JsonHubProtocol, LogLevel } from "@microsoft/signalr";
import { Cookies } from "react-cookie";
import { REACT_APP_HOST_ROUTE } from "src/app/chat/~api/apiRoutes";
import { ACCOUNT_HEADER, CHANNEL_TOKEN_HEADER, USER_ID_HEADER, PARENT_HEADER } from "src/app/chat/~store/actions/constants";
import { INegotiateResponse, serverProtocolEnum, signalRTransportProtocol } from "src/app/chat/~store/models/NegotiateResponse";
import { getExternalId, getUrlParam } from "src/app/shared/helpers/url-helper/UrlParamsHelper";
import { MessagePackHubProtocol, MessagePackOptions } from "@microsoft/signalr-protocol-msgpack";


declare global {
    interface Window {
        signalRHubConnection: HubConnection;
        serverUrl: string;
        serverProtocol: serverProtocolEnum;
        externalId: string;
        widgetOnScreen: boolean;
        signalRTransportProtocol: HttpTransportType;
        messagePack: boolean;
    }
}

const stateChangedSubscribers: Array<() => void> = [];
const fireConnectionStateChanged = () => {
    stateChangedSubscribers.forEach(s => s())
}

export const subscribeConnectionStateChanged = (subscriber: ()=>void) => {
    if(!stateChangedSubscribers.includes(subscriber))
        stateChangedSubscribers.push(subscriber);
}

export const unsubscribeConnectionStateChanged = (subscriber: ()=>void) => {
    const index = stateChangedSubscribers.indexOf(subscriber);
    if(index > -1) {
        stateChangedSubscribers.slice(index, 1);
    }
}

const trySendNegotiate = (hostUrl: string) => {
    try {
        console.log('Sending negotiate to server..');
        const response = sendNegotiateRequest(hostUrl);
        window.serverUrl = response.url;
        window.serverProtocol = response.protocol;
        window.signalRTransportProtocol = 
            response.signalRTransportProtocol === signalRTransportProtocol.serverSentEvents ? 
            HttpTransportType.ServerSentEvents : 
            HttpTransportType.WebSockets; 
        window.messagePack = response.messagePack;
        console.log(`Succeed to negotiate with server, signalRTransportProtocol: ${HttpTransportType[window.signalRTransportProtocol]}, messagePack: ${window.messagePack}`);
    }
    catch (err) {
        console.error(err.message);
        setTimeout(trySendNegotiate.bind(null, hostUrl), 2000);
    }
    
}

const sendNegotiateRequest = (hostUrl: string): INegotiateResponse => {
    const url = `${hostUrl}/negotiate?externalId=${getExternalId()}`;    
    const request = new XMLHttpRequest();
    request.open('GET', url, false);
    request.send(null);
    if (request.status === 200) {
        return JSON.parse(request.responseText);
    }
    throw 'Failed to negotiate with server';  
}

export const createSignalRConnection = (hostUrl: string) => {   
    trySendNegotiate(hostUrl);
    console.log('Creating SignalR connection..');
    const skipNegotiation = window.signalRTransportProtocol !== HttpTransportType.ServerSentEvents;

    const opt = {
        ingnoreUndefine: true,
        forceFloat32: true
    } as MessagePackOptions;
    const hubConnection = new HubConnectionBuilder()
        .withUrl(`${hostUrl}`, {skipNegotiation: skipNegotiation, transport: window.signalRTransportProtocol})
        .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (retryContext) => {
                const cookies = new Cookies();
                cookies.set('isReconnecting', skipNegotiation, { path: '/' });
                if (retryContext.previousRetryCount < 5) {
                    return retryContext.previousRetryCount * 2000 + (Math.random()*1000);
                } else {
                    return 10000;
                }
            }
        } as IRetryPolicy)
        .withHubProtocol(window.messagePack ? new MessagePackHubProtocol(opt) as IHubProtocol : new JsonHubProtocol())
        .configureLogging(LogLevel.Error).build();

    hubConnection.onclose((err) => {        
        const error = err === undefined ? '' : 'Error: ' + err;
        console.log('Connection closed! ' + error);
        fireConnectionStateChanged();
    });

    hubConnection.onreconnecting(fireConnectionStateChanged);
    hubConnection.onreconnected(fireConnectionStateChanged);

    hubConnection.on("Ping", (pingTime: any) => pong(pingTime));

    window.signalRHubConnection = hubConnection;
    return hubConnection;
}

export const startSignalRConnection = async (hubConnection: HubConnection) => {
    try {
        if(hubConnection.state === HubConnectionState.Connected) {return;}
        const account = getUrlParam(ACCOUNT_HEADER);
        const userid =  getUrlParam(USER_ID_HEADER);
        const parent = getUrlParam(PARENT_HEADER);
        const token =  getUrlParam(CHANNEL_TOKEN_HEADER);
        hubConnection.baseUrl = `${window.serverUrl}/chat?externalId=${getExternalId()}&token=${token}&userid=${userid}&account=${account}&parent=${parent}`;
        startSignalRConnection2(hubConnection);
    } catch (err) {
        console.log('Error while establishing connection: ' + err);
        setTimeout(() => startSignalRConnection(hubConnection), 2000);
    }
};

export const startSignalRConnection2 = async (hubConnection: HubConnection) => {
    if(hubConnection.state === HubConnectionState.Connected) {return;}

    // Timeout for server activity. 
    // The recommended value is a number at least double the server's 
    // KeepAliveInterval value to allow time for pings to arrive
    hubConnection.serverTimeoutInMilliseconds = 15000;

    // Determines the interval at which the client sends ping messages
    // As we using server side pings, we don't need to send pings from the client.
    // We cannot set null or 0 (min value 5 seconds), so just set big number
    hubConnection.keepAliveIntervalInMilliseconds = 1000000;
    console.log('Connecting..');
    try {
        await hubConnection.start();
        fireConnectionStateChanged();
        console.log('Connection started!');
    } catch (err) {
        console.log('Error while establishing connection: ' + err);
        setTimeout(() => startSignalRConnection(hubConnection), 2000);
    }
};

let pongTimeout: NodeJS.Timeout;

const pong = (pingTime: Date) => {
    window.signalRHubConnection.send("Pong", pingTime);
    clearTimeout(pongTimeout);

    pongTimeout = setTimeout(() => {
        console.info("SignalR reconnecting due to a one minute we dont get pings.")
        try{
            window.signalRHubConnection.stop();
        }catch{}
        
        startSignalRConnection(window.signalRHubConnection);
        
    }, 60000);
};